import {
    useGetArchivedFlashInfosMutation,
    useToggleArchiveFlashInfoMutation,
    useGetFlashInfosMutation,
    useToggleFlashInfoActiveMutation
} from '@services/member/flash-info/flash-info.endpoints';
import { PaginationMetadata } from '@services/providers/cosem.types';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useSelector } from 'react-redux';
import { getUser } from '@features/auth/auth.slice';
import { FlashInfoTable } from '@services/member/flash-info/flash-info.types';

export const useListPageVM = () => {
    const MySwal = withReactContent(Swal);

    const [totalFlashInfos, setTotalFlashInfos] = useState<number>(0);
    const [isArchived, setIsArchived] = useState<boolean>(false);
    const columnHelper = createColumnHelper<FlashInfoTable>();
    const user = useSelector(getUser);
    const navigate = useNavigate();
    const [needReload, setNeedReload] = useState<boolean>();

    const [toggleArchiveFlashInfo, { isSuccess: isToggleArchiveFlashInfoSuccess, isLoading: isToggleArchiveFlashInfoLoading }] =
        useToggleArchiveFlashInfoMutation();
    const [toggleFlashInfoActive, { isSuccess: isToggleFlashInfoActiveSuccess, isLoading: isToggleFlashInfoActiveLoading }] =
        useToggleFlashInfoActiveMutation();
    const mutationHook = useMemo(() => {
        return isArchived ? useGetArchivedFlashInfosMutation : useGetFlashInfosMutation;
    }, [isArchived]);

    useEffect(() => {
        setTotalFlashInfos(0);
    }, [isArchived]);

    useEffect(() => {
        if (isToggleArchiveFlashInfoSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isToggleArchiveFlashInfoSuccess]);

    useEffect(() => {
        if (isToggleFlashInfoActiveSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isToggleFlashInfoActiveSuccess]);

    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalFlashInfos(metadata.total);
        }
    };

    const handleToggleFlashInfoActive = (id: number, value: boolean) => {
        MySwal.fire({
            title: !value
                ? 'Êtes-vous sûr de vouloir activer cette information ?'
                : 'Êtes-vous sûr de vouloir désactiver cette information ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: !value ? 'Activer' : 'Désactiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                toggleFlashInfoActive({ id });
            }
        });
    };

    const handleToggleArchiveFlashInfo = (id: number, archive: boolean) => {
        MySwal.fire({
            title: archive
                ? 'Êtes-vous sûr de vouloir archiver cette information ?'
                : 'Êtes-vous sûr de vouloir désarchiver cette information ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                toggleArchiveFlashInfo({ id, archived: archive });
            }
        });
    };

    const handleToggleArchiveFlashInfos = (lines: FlashInfoTable[], archive: boolean) => {
        MySwal.fire({
            title: archive
                ? 'Êtes-vous sûr de vouloir archiver ces informations ?'
                : 'Êtes-vous sûr de vouloir désarchiver ces informations ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                lines.forEach((line) => {
                    toggleArchiveFlashInfo({ id: line.id, archived: archive });
                });
            }
        });
    };

    return {
        handleToggleArchiveFlashInfo,
        handleToggleArchiveFlashInfos,
        handleToggleFlashInfoActive,
        handleMetadata,
        navigate,
        columnHelper,
        isArchived,
        mutationHook,
        setIsArchived,
        totalFlashInfos,
        isLoading: isToggleArchiveFlashInfoLoading || isToggleFlashInfoActiveLoading,
        needReload,
        user
    };
};
